import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MonAbonnementPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/api/customer/request-portal-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Une erreur est survenue");
      }

      setSent(true);
    } catch (err) {
      setError(err.message || "Impossible d'envoyer le lien. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-32 pb-16 px-4" data-testid="mon-abonnement-page">
      <div className="container mx-auto max-w-2xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#52a675]/10 mb-4">
            <ShieldCheck className="w-8 h-8 text-[#52a675]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">
            Mon abonnement
          </h1>
          <p className="text-gray-600 text-base">
            Accédez aux informations de votre contrat de manière sécurisée.
          </p>
        </div>

        <Card className="border-0 shadow-xl">
          {!sent ? (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">Demander un accès</CardTitle>
                <CardDescription>
                  Saisissez l'email associé à votre abonnement. Nous vous enverrons
                  un lien sécurisé pour consulter votre contrat.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="portal-email">Adresse email</Label>
                    <Input
                      id="portal-email"
                      type="email"
                      placeholder="nom@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      data-testid="portal-email-input"
                      className="h-12"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" data-testid="portal-error-message">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full h-12 bg-[#52a675] hover:bg-[#3d8a5c] text-white font-medium"
                    data-testid="portal-request-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Recevoir mon lien d'accès
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-gray-500 mt-4">
                    Le lien envoyé est valable 30 minutes et ne doit pas être partagé.
                  </p>
                </form>
              </CardContent>
            </>
          ) : (
            <CardContent className="pt-12 pb-12 text-center" data-testid="portal-email-sent-confirmation">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-5">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Vérifiez votre boîte mail
              </h2>
              <p className="text-gray-600 mb-2">
                Si votre email est enregistré chez nous, un lien d'accès vient de vous être envoyé à :
              </p>
              <p className="font-semibold text-gray-900 mb-6">{email}</p>
              <p className="text-sm text-gray-500">
                Pensez à vérifier votre dossier "spam" si vous ne le voyez pas.
              </p>
              <Button
                variant="outline"
                onClick={() => { setSent(false); setEmail(''); }}
                className="mt-6"
                data-testid="portal-resend-button"
              >
                Utiliser un autre email
              </Button>
            </CardContent>
          )}
        </Card>

        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Pour toute résiliation ou modification de votre contrat,</p>
          <p>
            contactez-nous à{' '}
            <a href="mailto:info@car-main.ch" className="text-[#52a675] font-medium hover:underline">
              info@car-main.ch
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MonAbonnementPage;
